import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Building, 
  TrendingUp, 
  Users, 
  MapPin, 
  Calendar,
  BarChart3,
  PieChart,
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  Target
} from 'lucide-react';
import { facilityAPI } from '../../services/facilityApi';
import { FacilityStatistics, FacilityAssessmentStats } from '../../types/facility';

const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const StatTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  margin: 0;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
`;

const StatChange = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #ef4444;
  display: flex;
  align-items: center;
  gap: 4px;

  &[data-positive='true'] {
    color: #10b981;
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const ChartCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const ChartContent = styled.div`
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 14px;
`;

const FacilitiesList = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const ListHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ListTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const ListContent = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const FacilityItem = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: #f9fafb;
  }
`;

const FacilityInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FacilityName = styled.div`
  font-weight: 600;
  color: #111827;
`;

const FacilityLocation = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const FacilityStats = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const StatBadge = styled.div<{ color: string }>`
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.color};
  color: white;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #dc2626;
  padding: 16px;
  border-radius: 8px;
  margin: 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyIcon = styled(Building)`
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  color: #d1d5db;
`;

const FacilityStatsComponent: React.FC = () => {
  const [stats, setStats] = useState<FacilityStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const statsData = await facilityAPI.getFacilityStatistics();
      setStats(statsData);
    } catch (err) {
      setError('Failed to load facility statistics');
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>
          <AlertTriangle size={20} />
          {error}
        </ErrorMessage>
      </Container>
    );
  }

  if (!stats) {
    return (
      <Container>
        <EmptyState>
          <EmptyIcon />
          <h3>No statistics available</h3>
          <p>Unable to load facility statistics at this time.</p>
        </EmptyState>
      </Container>
    );
  }

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'Hospital': '#3b82f6',
      'Clinic': '#10b981',
      'Nursing Home': '#f59e0b',
      'Rehabilitation Center': '#8b5cf6',
      'Urgent Care': '#ef4444',
      'Specialty Center': '#06b6d4',
      'Other': '#6b7280'
    };
    return colors[type] || '#6b7280';
  };

  const getStateColor = (index: number) => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
    return colors[index % colors.length];
  };

  return (
    <Container>
      <Header>
        <Title>
          <BarChart3 size={32} />
          Facility Statistics
        </Title>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatIcon color="#3b82f6">
              <Building size={24} />
            </StatIcon>
            <StatTitle>Total Facilities</StatTitle>
          </StatHeader>
          <StatValue>{stats.totalFacilities}</StatValue>
          <StatChange data-positive="true">
            <TrendingUp size={16} />
            +12% from last month
          </StatChange>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="#10b981">
              <CheckCircle size={24} />
            </StatIcon>
            <StatTitle>Active Facilities</StatTitle>
          </StatHeader>
          <StatValue>{stats.activeFacilities}</StatValue>
          <StatChange data-positive="true">
            <TrendingUp size={16} />
            {((stats.activeFacilities / stats.totalFacilities) * 100).toFixed(1)}% of total
          </StatChange>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="#f59e0b">
              <Clock size={24} />
            </StatIcon>
            <StatTitle>Pending Facilities</StatTitle>
          </StatHeader>
          <StatValue>0</StatValue>
          <StatChange>
            <Activity size={16} />
            Awaiting approval
          </StatChange>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="#ef4444">
              <AlertTriangle size={24} />
            </StatIcon>
            <StatTitle>Inactive Facilities</StatTitle>
          </StatHeader>
          <StatValue>{stats.inactiveFacilities}</StatValue>
          <StatChange>
            <TrendingUp size={16} />
            {((stats.inactiveFacilities / stats.totalFacilities) * 100).toFixed(1)}% of total
          </StatChange>
        </StatCard>
      </StatsGrid>

      <ChartsGrid>
        <ChartCard>
          <ChartHeader>
            <PieChart size={20} />
            <ChartTitle>Facilities by Type</ChartTitle>
          </ChartHeader>
          <ChartContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
              {Object.entries(stats.facilitiesByType).map(([type, count]) => (
                <div key={type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div 
                      style={{ 
                        width: '12px', 
                        height: '12px', 
                        borderRadius: '50%', 
                        backgroundColor: getTypeColor(type) 
                      }} 
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>{type}</span>
                  </div>
                  <span style={{ fontWeight: '600', color: '#111827' }}>{count}</span>
                </div>
              ))}
            </div>
          </ChartContent>
        </ChartCard>

        <ChartCard>
          <ChartHeader>
            <MapPin size={20} />
            <ChartTitle>Top States</ChartTitle>
          </ChartHeader>
          <ChartContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
              {stats.facilitiesByState.slice(0, 8).map((state, index) => (
                <div key={state.state} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div 
                      style={{ 
                        width: '12px', 
                        height: '12px', 
                        borderRadius: '50%', 
                        backgroundColor: getStateColor(index) 
                      }} 
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>{state.state}</span>
                  </div>
                  <span style={{ fontWeight: '600', color: '#111827' }}>{state.count}</span>
                </div>
              ))}
            </div>
          </ChartContent>
        </ChartCard>
      </ChartsGrid>

      <FacilitiesList>
        <ListHeader>
          <Target size={20} />
          <ListTitle>Top Performing Facilities</ListTitle>
        </ListHeader>
        <ListContent>
          {stats.topPerformingFacilities.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              No performance data available
            </div>
          ) : (
            stats.topPerformingFacilities.map((facility) => (
              <FacilityItem key={facility.facilityId}>
                <FacilityInfo>
                  <FacilityName>{facility.facilityName}</FacilityName>
                  <FacilityLocation>
                    {facility.totalAssessments} assessments • Avg Readmission Probability: {(facility.averageReadmissionProbability ?? facility.averageScore).toFixed(1)}%
                  </FacilityLocation>
                </FacilityInfo>
                <FacilityStats>
                  <StatBadge color="#10b981">
                    {facility.lowRiskPercentage.toFixed(1)}% Low Risk
                  </StatBadge>
                </FacilityStats>
              </FacilityItem>
            ))
          )}
        </ListContent>
      </FacilitiesList>

      <FacilitiesList style={{ marginTop: '24px' }}>
        <ListHeader>
          <Calendar size={20} />
          <ListTitle>Recently Added Facilities</ListTitle>
        </ListHeader>
        <ListContent>
          {stats.recentFacilities.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              No recent facilities
            </div>
          ) : (
            stats.recentFacilities.map((facility) => (
              <FacilityItem key={facility.id}>
                <FacilityInfo>
                  <FacilityName>{facility.name}</FacilityName>
                  <FacilityLocation>
                    {facility.address.city}, {facility.address.state} • {facility.type}
                  </FacilityLocation>
                </FacilityInfo>
                <FacilityStats>
                  <StatBadge color={facility.status === 'Active' ? '#10b981' : '#f59e0b'}>
                    {facility.status}
                  </StatBadge>
                </FacilityStats>
              </FacilityItem>
            ))
          )}
        </ListContent>
      </FacilitiesList>
    </Container>
  );
};

export default FacilityStatsComponent;
